import { Controller } from "react-hook-form";
import { TextField, Typography, MenuItem, Box } from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ORGANIZATION_TIER_TYPES,
  sale_categories,
} from "@/modules/common/constants";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { SingleAutoCompleteSelectChip } from "@/modules/common/components/input/single-autocomplete-select";
import useSaleForm from "@/modules/lead-page/hooks/lead-info/status/forms/sale-form.hook";
import FormFooter from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-footer";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import { StaticFormProps } from "@/modules/lead-page/types";
import {
  EnvironmentVariables,
  LocationDataParams,
  Options,
} from "@/modules/common/types";
import useLocationsData from "@/modules/common/hooks/locations-data.hook";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import MoneyInput from "@/modules/common/components/input/money-input";
import { finishingOptions } from "@/modules/lead-page/constants";
import { useMemo, useState } from "react";
import { LocationsResponse } from "@/modules/common/dtos/index.v2";
import ReadOnlyTextInput from "@/modules/common/components/input/readonly-text-input";
import { useManageReferrals } from "@/modules/lead-page/hooks/manage-referrals.hook";
import Grid from "@mui/material/GridLegacy";
import { SaleDocumentType } from "@/modules/leads/constants";
import { handleSaleCategoryChange } from "@/modules/lead-page/utils";
import { getReferralLabel } from "@/modules/common/utils";

const SaleForm = ({
  handleSaleSubmission,
  handleClose,
  saleType,
  sale,
  handleDeleteFile,
  handleFiles,
  handlePaymentFiles,
  handlePaymentPlanFiles,
  handleDeletePaymentFiles,
  files,

  isLoadingSubmition,
}: StaticFormProps) => {
  const showPaymentDocuments =
    `${EnvironmentVariables.COMPANY_NAME}` ===
      ORGANIZATION_TIER_TYPES.taskeen ||
    `${EnvironmentVariables.COMPANY_NAME}` === ORGANIZATION_TIER_TYPES.nawy;

  const [selectedUnitLocation, setSelectedUnitLocation] = useState<
    Options | undefined
  >();
  const {
    errors,
    control,
    onSubmit,
    handleSubmit,
    selectedCompound,
    propertyTypeOptions,
    selectedSaleCategory,
    selectedPropertyType,
    saleDocumentTypeOption,
    setSaleDocumentTypeOption,
  } = useSaleForm({
    selectedUnitLocation,
    handleSaleSubmission,
    existingSale: sale || undefined,
  });

  const { referralsData } = useManageReferrals();
  const leadHasActiveReferral = referralsData?.data?.find(
    (referral) => referral.isExpired === false,
  );

  const {
    locationsData: compoundsData,
    locationsLoading: compoundsLoading,
    locationSearchTerm: compoundsSearchTerm,
    handleSearchChange: handleCompoundSearchChange,
  } = useLocationsData({
    locationDataParams: LocationDataParams.COMPOUND,
  });

  const compoundOptions = useMemo(() => {
    const mapCompoundToOption = (
      location: LocationsResponse["data"]["results"][number],
    ) => ({
      value: location?.compound?.uid,
      optionValueUid: location?.compound?.area?.uid,
      label: `${location?.compound?.name} - ${location?.compound?.area?.name}`,
      developerName: location?.compound?.developer?.name ?? "",
    });

    const baseOptions = (compoundsData?.results || []).map(mapCompoundToOption);

    const saleCompoundOption = sale?.compound
      ? {
          value: sale.compound.uid,
          optionValueUid: sale.area?.uid,
          label: `${sale.compound.name} - ${sale.area?.name}`,
          developerName: sale.compound.developer?.name ?? "",
        }
      : null;

    return saleCompoundOption &&
      !baseOptions.find((opt) => opt.value === saleCompoundOption.value)
      ? [...baseOptions, saleCompoundOption]
      : baseOptions;
  }, [compoundsData?.results, sale?.compound, sale?.area]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3_med">
          {sale ? "Edit" : "New"} {saleType} Form
        </Typography>
        <Typography variant="h4_med">
          {getReferralLabel({ sale, leadHasActiveReferral })}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item mobile={12} tablet={6} data-testid="sale-unit-value">
          <Controller
            name="unitValue"
            control={control}
            render={({ field }) => (
              <MoneyInput
                label="Unit Value"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                isError={!!errors.unitValue}
                helperText={errors.unitValue?.message}
              />
            )}
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-unit-number">
          <Controller
            name="unitNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Unit Number"
                InputLabelProps={{
                  shrink: field.value ? true : false,
                }}
                fullWidth
                error={!!errors.unitNumber}
                helperText={errors.unitNumber?.message}
              />
            )}
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-unit-area">
          <Controller
            name="unitArea"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Unit Area (m²)"
                InputLabelProps={{
                  shrink: field.value ? true : false,
                }}
                type="number"
                inputProps={{ min: 0 }}
                fullWidth
                error={!!errors.unitArea}
                helperText={errors.unitArea?.message}
              />
            )}
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-finishing">
          <Controller
            name="finishing"
            control={control}
            render={({ field }) => (
              <TextField
                select
                {...field}
                label="Finishing"
                fullWidth
                error={!!errors.finishing}
                helperText={errors.finishing?.message}
              >
                {finishingOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label.charAt(0).toUpperCase() +
                      option.label.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-compound">
          <SingleSelectSearch
            value={selectedCompound}
            options={compoundOptions}
            isLoading={compoundsLoading}
            searchValue={compoundsSearchTerm}
            size="medium"
            onSearchValueChange={handleCompoundSearchChange}
            name="compoundUId"
            control={control}
            label="Compound"
            error={errors.compoundUId?.message}
            defaultValue={sale?.compound?.uid}
            onValueChange={(value) => {
              const selectedOption = compoundOptions.find(
                (opt) => opt.value === value,
              );
              setSelectedUnitLocation(selectedOption);
            }}
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-developer">
          <ReadOnlyTextInput
            label="Developer"
            value={
              selectedUnitLocation?.developerName ||
              sale?.compound?.developer?.name ||
              ""
            }
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-down-payment">
          <Controller
            name="downPayment"
            control={control}
            render={({ field }) => (
              <MoneyInput
                label="Down Payment"
                onChange={(e) => field.onChange(e.target.value)}
                value={field.value}
                isError={!!errors.downPayment}
                helperText={errors.downPayment?.message}
              />
            )}
          />
        </Grid>

        <Grid item mobile={12} tablet={6} data-testid="sale-property-type">
          <SingleAutoCompleteSelectChip
            name="propertyTypeUId"
            control={control}
            value={selectedPropertyType || sale?.propertyType?.uid}
            options={propertyTypeOptions}
            label="Property Type"
            error={errors.propertyTypeUId?.message}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-date-reservation">
          <Controller
            name="dateOfReservation"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Reservation"
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfReservation,
                      helperText: errors.dateOfReservation?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-date-contraction">
          <Controller
            name="dateOfContraction"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Contraction"
                  format="dd/MM/yyyy"
                  value={field.value}
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfContraction,
                      helperText: errors.dateOfContraction?.message,
                    },
                  }}
                  disabled
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-actual-date-contraction">
          <Controller
            name="actualDateOfContraction"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Actual Date of Contraction"
                  value={field.value}
                  format="dd/MM/yyyy"
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.actualDateOfContraction,
                      helperText: errors.actualDateOfContraction?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-date-down-payment">
          <Controller
            name="dateOfDownPayment"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Down Payment"
                  value={field.value}
                  format="dd/MM/yyyy"
                  onChange={(newValue: Date | null) => {
                    const adjustedDate = newValue
                      ? new Date(newValue.setHours(12, 0, 0, 0))
                      : null;
                    field.onChange(adjustedDate);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfDownPayment,
                      helperText: errors.dateOfDownPayment?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-years-of-payment">
          <Controller
            name="yearsOfPayment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Years of Payment"
                InputLabelProps={{
                  shrink: field.value ? true : false,
                }}
                type="number"
                inputProps={{ min: 0 }}
                fullWidth
                error={!!errors.yearsOfPayment}
                helperText={errors.yearsOfPayment?.message}
              />
            )}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-category">
          <SingleAutoCompleteSelectChip
            name="saleCategory"
            control={control}
            value={selectedSaleCategory}
            options={sale_categories}
            onChange={(value) => {
              handleSaleCategoryChange(value, setSaleDocumentTypeOption);
            }}
            label="Sale Category"
            error={errors.saleCategory?.message}
          />
        </Grid>
        <Grid item mobile={12} tablet={6} data-testid="sale-name-on-contract">
          <Controller
            name="nameOnContract"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name on Contract"
                InputLabelProps={{
                  shrink: field.value ? true : false,
                }}
                fullWidth
                error={!!errors.nameOnContract}
                helperText={errors.nameOnContract?.message}
              />
            )}
          />
        </Grid>
        {selectedSaleCategory === "Nawy Now" && (
          <Grid item mobile={12} tablet={6}>
            <Controller
              name="cashPrice"
              control={control}
              render={({ field }) => (
                <MoneyInput
                  {...field}
                  label="Cash Price"
                  value={field.value || undefined}
                  onChange={(e) => field.onChange(e.target.value)}
                  isError={!!errors.cashPrice}
                  helperText={errors.cashPrice?.message}
                />
              )}
            />
          </Grid>
        )}
        <Grid item mobile={12} data-testid="sale-comment">
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="normal"
                label="Comment"
                InputLabelProps={{
                  shrink: field.value ? true : false,
                }}
                multiline
                rows={4}
                error={!!errors.comment}
                helperText={errors.comment?.message}
                inputProps={{ minLength: 4 }} // Added minLength attribute
              />
            )}
          />
        </Grid>

        {showPaymentDocuments && (
          <>
            {saleDocumentTypeOption === SaleDocumentType.PAYMENT && (
              <Grid
                item
                mobile={12}
                data-testid="sale-upload-payment"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="h4_med">
                  Upload Payment Documents *
                </Typography>
                <FileUploadButton
                  setFiles={handlePaymentFiles}
                  acceptedFileTypes={[
                    "image/jpeg",
                    "image/png",
                    "application/pdf",
                  ]}
                  onDeleteFile={handleDeletePaymentFiles}
                />
              </Grid>
            )}
            {saleDocumentTypeOption === SaleDocumentType.PAYMENT_PLAN && (
              <Grid
                item
                mobile={12}
                data-testid="sale-upload-payment-plans"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="h4_med">
                  Upload Payment Plans Documents *
                </Typography>
                <FileUploadButton
                  setFiles={handlePaymentPlanFiles}
                  acceptedFileTypes={[
                    "image/jpeg",
                    "image/png",
                    "application/pdf",
                  ]}
                  onDeleteFile={handleDeletePaymentFiles}
                />
              </Grid>
            )}
          </>
        )}

        <Grid
          item
          mobile={12}
          data-testid="sale-upload-documents"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="h4_med">Upload Documents *</Typography>
          <FileUploadButton
            setFiles={handleFiles}
            acceptedFileTypes={["image/jpeg", "image/png", "application/pdf"]}
            onDeleteFile={handleDeleteFile}
          />
        </Grid>
      </Grid>
      <FormFooter
        handleClose={handleClose}
        isLoadingSubmition={isLoadingSubmition}
        confirmText={sale ? "Update" : "Save"}
        disabled={files?.length === 0}
      />
    </form>
  );
};

export default SaleForm;
